import {
  string as yupString,
  mixed as yupMixed,
} from 'yup';

import InputFormik from '../../shared/forms/input/inputFormik.component';
import SelectFormik from '../../shared/forms/select/sdkSelectFormik.component';
import TextAreaFormik from '../../shared/forms/textarea/textareaFormik.component';
import AttachmentsFormik from '../../shared/forms/attachments/attachmentsFormik.component';
import { required, maxLength } from '../../shared/forms/validators';
import { decimalWithSignFormatter } from './addEditAdjustmentModal.formatters';
import { hideField } from '../../shared/forms/constants';
import { NO_FLAG } from '../../shared/constants';

import { frequencyOptions, annualCumulativeOptions } from './adjustmentForm.constants';

import AmountInfo from './amountInfo.component';
import MultiTaxNonUSTaxAmountInfo from './multiTaxNonUSTaxAmountInfo.component';
import AddEditAdjustmentCalendar from './calendar/addEditAdjustmentCalendar.component';

import {
  isManuallyEntered,
  requiredWhenManuallyEntered,
  requiredWhenAccountInTrialBalance,
  isNotPreTaxAdjustmentTypeSelected,
  requiredWhenNotPreTax,
  requiredWhenManuallyEnteredAndRecurring,
  isManuallyEnteredAndRecurring,
  requiredWhenBookToTaxDescriptionVisible,
  isBookToTaxDescriptionRequired,
  showMultiTaxNonUSTaxAmountInfo,
} from './adjustmentForm.helpers';

const schema = [
  {
    name: 'countryTaxType',
    component: SelectFormik,
    meta: { label: 'Country Tax Type', optionsKey: 'countryTaxTypes' },
    options: { yupType: yupString(), validationRules: [required] },
    additionalProps: { required: true },
  },
  {
    name: 'adjustmentType',
    component: SelectFormik,
    meta: { label: 'Adjustment Type', optionsKey: 'adjustmentTypeOptions' },
    options: { yupType: yupString(), validationRules: [required] },
    additionalProps: { required: true, clearWhenOptionsChanged: true },
  },
  {
    name: 'multiTaxNonUSTaxAmountInfo',
    component: MultiTaxNonUSTaxAmountInfo,
    options: { readOnly: true },
    conditions: [
      {
        when: showMultiTaxNonUSTaxAmountInfo,
        then: {},
        otherwise: hideField,
      },
    ],
  },
  {
    name: 'account',
    component: SelectFormik,
    meta: { label: 'Account', optionsKey: 'accountOptions' },
    options: { yupType: yupString(), validationRules: [requiredWhenNotPreTax] },
    additionalProps: (formContext, formik) => ({
      virtualized: true,
      onSelect: value => {
        const account = formContext.accountOptions.find(acc => acc.value === value);
        formik.setFieldValue('description', account.label);
      },
    }),
    conditions: [
      {
        when: isNotPreTaxAdjustmentTypeSelected,
        then: {
          additionalProps: { required: true },
        },
      },
    ],
  },
  {
    name: 'description',
    component: TextAreaFormik,
    meta: { label: 'Description' },
    options: { yupType: yupString(), validationRules: [required, maxLength(240)] },
    additionalProps: { required: true, resize: 'vertical', rows: 1 },
  },
  {
    name: 'amountFrom',
    component: SelectFormik,
    meta: { label: 'Amount From', optionsKey: 'amountFromOptions', initialValue: NO_FLAG },
    options: { yupType: yupString(), validationRules: [required] },
    additionalProps: { required: true, clearWhenOptionsChanged: true },
  },
  {
    name: 'frequency',
    component: SelectFormik,
    meta: { label: 'Frequency', options: frequencyOptions, initialValue: NO_FLAG },
    options: { yupType: yupString(), validationRules: [required] },
    additionalProps: { required: true },
  },
  {
    name: 'amount',
    component: InputFormik,
    meta: { label: 'Amount' },
    options: { yupType: yupMixed(), validationRules: [requiredWhenManuallyEntered] },
    additionalProps: { valueFormatter: decimalWithSignFormatter },
    conditions: [
      {
        when: isManuallyEntered,
        then: {
          additionalProps: { required: true },
        },
        otherwise: hideField,
      },
    ],
  },
  {
    name: 'amountInfo',
    component: AmountInfo,
    options: { readOnly: true },
  },
  {
    name: 'multiplier',
    component: InputFormik,
    meta: { label: 'Multiplier' },
    options: { yupType: yupMixed(), validationRules: [requiredWhenAccountInTrialBalance] },
    additionalProps: { suffix: '%', valueFormatter: decimalWithSignFormatter },
    conditions: [
      {
        when: isManuallyEntered,
        then: hideField,
        otherwise: {
          additionalProps: { required: true },
        },
      },
    ],
  },
  {
    name: 'annualCum',
    component: SelectFormik,
    meta: { label: 'Annual Cumulative', options: annualCumulativeOptions },
    options: { yupType: yupMixed(), validationRules: [requiredWhenManuallyEnteredAndRecurring] },
    conditions: [
      {
        when: isManuallyEnteredAndRecurring,
        then: {
          additionalProps: { required: true },
        },
        otherwise: hideField,
      },
    ],
  },
  {
    name: 'bookToTaxDescription',
    component: SelectFormik,
    meta: { label: 'Book to Tax Description', optionsKey: 'bookTypes' },
    options: { yupType: yupMixed(), validationRules: [requiredWhenBookToTaxDescriptionVisible] },
    additionalProps: { virtualized: true },
    conditions: [
      {
        when: isBookToTaxDescriptionRequired,
        then: {
          additionalProps: { required: true },
        },
        otherwise: hideField,
      },
    ],
  },
  {
    name: 'files',
    component: AttachmentsFormik,
    meta: { label: 'Attachments', initialValue: [] },
    options: { yupType: yupMixed() },
    additionalProps: {
      valueFormatter: file => file,
      multiple: true,
      acceptTypes: ['.xls', '.xlsx', '.doc', '.docx', '.ppt', '.pptx', '.pdf', '.txt'],
    },
  },
  {
    name: 'calendar',
    component: AddEditAdjustmentCalendar,
    meta: { valueGetter: ({ formik, formContext }) => ({
      amount: formik.values.amount,
      periodName: formContext.period.name,
      firstMonth: formContext.firstMonth,
      frequency: formik.values.frequency,
      annualCum: formik.values.annualCum,
    }) },
    options: { readOnly: true },
    conditions: [
      {
        when: isManuallyEntered,
        then: {},
        otherwise: hideField,
      },
    ],
  },
];

export default schema;
